import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import toast from 'react-hot-toast'
import { user as userService, EmailSequenceInfo, EmailTemplateInfo } from '@/services/user/user-service'
import useAppState from '@/contexts/state'
import { ChevronDown, Mail, Clock, Eye, Send, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'

type Mode = 'select' | 'preview' | 'result'

interface SendResult {
  message: string
  sequenceName: string
  successCount: number
  failureCount: number
  immediateCount: number
  scheduledCount: number
}

export default function SequenceSender() {
  const [mode, setMode] = useState<Mode>('select')
  const [sequences, setSequences] = useState<EmailSequenceInfo[]>([])
  const [selectedSequence, setSelectedSequence] = useState<EmailSequenceInfo | null>(null)
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([])
  const [selectAll, setSelectAll] = useState(true)
  const [emails, setEmails] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [previewHtml, setPreviewHtml] = useState('')
  const [previewTemplateIndex, setPreviewTemplateIndex] = useState(0)
  const [sendResult, setSendResult] = useState<SendResult | null>(null)
  const user = useAppState(state => state.user)

  const emailList = emails
    .split(',')
    .map(e => e.trim())
    .filter(Boolean)

  // Load sequences on mount
  useEffect(() => {
    userService
      .getEmailSequences()
      .then(res => {
        setSequences(res.sequences)
        if (res.sequences.length > 0) {
          setSelectedSequence(res.sequences[0])
          setSelectedTemplateIds(res.sequences[0].templates.map(t => t.id))
        }
      })
      .catch(error => {
        console.error('Error loading sequences:', error)
        toast.error('Failed to load email sequences')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [])

  // Set user email as default
  useEffect(() => {
    setEmails(user?.email || '')
  }, [user])

  // Update template selection when "Select All" changes
  useEffect(() => {
    if (selectedSequence) {
      if (selectAll) {
        setSelectedTemplateIds(selectedSequence.templates.map(t => t.id))
      }
    }
  }, [selectAll, selectedSequence])

  const handleSequenceSelect = (sequence: EmailSequenceInfo) => {
    setSelectedSequence(sequence)
    setSelectedTemplateIds(sequence.templates.map(t => t.id))
    setSelectAll(true)
  }

  const handleTemplateToggle = (templateId: string) => {
    setSelectedTemplateIds(prev => {
      if (prev.includes(templateId)) {
        const newIds = prev.filter(id => id !== templateId)
        setSelectAll(newIds.length === selectedSequence?.templates.length)
        return newIds
      } else {
        const newIds = [...prev, templateId]
        setSelectAll(newIds.length === selectedSequence?.templates.length)
        return newIds
      }
    })
  }

  const handleSelectAllToggle = () => {
    if (selectAll) {
      setSelectedTemplateIds([])
      setSelectAll(false)
    } else {
      setSelectedTemplateIds(selectedSequence?.templates.map(t => t.id) || [])
      setSelectAll(true)
    }
  }

  const getSelectedTemplates = (): EmailTemplateInfo[] => {
    if (!selectedSequence) return []
    return selectedSequence.templates.filter(t => selectedTemplateIds.includes(t.id))
  }

  const handlePreview = async () => {
    if (!selectedSequence || selectedTemplateIds.length === 0) {
      toast.error('Please select at least one template')
      return
    }

    if (!emails.trim()) {
      toast.error('Please enter at least one email address')
      return
    }

    const templates = getSelectedTemplates()
    setPreviewTemplateIndex(0)

    try {
      const preview = await userService.previewSequenceEmail({
        sequenceId: selectedSequence.id,
        templateId: templates[0].id,
        firstName: 'Customer',
      })
      setPreviewHtml(preview.html)
      setMode('preview')
    } catch (error) {
      console.error('Error loading preview:', error)
      toast.error('Failed to load email preview')
    }
  }

  const handlePreviewTemplateChange = async (index: number) => {
    if (!selectedSequence) return
    const templates = getSelectedTemplates()
    const template = templates[index]

    try {
      const preview = await userService.previewSequenceEmail({
        sequenceId: selectedSequence.id,
        templateId: template.id,
        firstName: 'Customer',
      })
      setPreviewHtml(preview.html)
      setPreviewTemplateIndex(index)
    } catch (error) {
      console.error('Error loading preview:', error)
      toast.error('Failed to load email preview')
    }
  }

  const handleSend = async () => {
    if (!selectedSequence) return

    setIsSending(true)

    try {
      const result = await userService.sendSequenceEmails({
        emails: emailList,
        sequenceId: selectedSequence.id,
        templateIds: selectAll ? undefined : selectedTemplateIds,
      })

      setSendResult({
        message: result.message,
        sequenceName: result.sequenceName,
        successCount: result.successCount,
        failureCount: result.failureCount,
        immediateCount: result.immediateCount,
        scheduledCount: result.scheduledCount,
      })
      setMode('result')
    } catch (error) {
      console.error('Error sending emails:', error)
      toast.error('Failed to send emails')
    } finally {
      setIsSending(false)
    }
  }

  const handleReset = () => {
    setMode('select')
    setSendResult(null)
    setPreviewHtml('')
    setPreviewTemplateIndex(0)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Result view
  if (mode === 'result' && sendResult) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {sendResult.failureCount === 0 ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <AlertCircle className="h-5 w-5 text-yellow-500" />
              )}
              Emails Sent
            </CardTitle>
            <CardDescription>{sendResult.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="rounded-lg border p-4 text-center">
                <p className="text-2xl font-bold text-green-600">{sendResult.successCount}</p>
                <p className="text-xs text-muted-foreground">Successful</p>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{sendResult.immediateCount}</p>
                <p className="text-xs text-muted-foreground">Sent Immediately</p>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <p className="text-2xl font-bold text-purple-600">{sendResult.scheduledCount}</p>
                <p className="text-xs text-muted-foreground">Scheduled</p>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{sendResult.failureCount}</p>
                <p className="text-xs text-muted-foreground">Failed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Button onClick={handleReset}>Send Another Sequence</Button>
      </div>
    )
  }

  // Preview view
  if (mode === 'preview') {
    const templates = getSelectedTemplates()

    return (
      <div className="space-y-6">
        <div>
          <h2 className="mb-2 text-xl font-semibold">Preview & Confirm</h2>
          <p className="text-muted-foreground">
            Review the emails before sending to {emailList.length} recipient{emailList.length !== 1 ? 's' : ''}
          </p>
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Sequence</h3>
                <p className="text-sm">{selectedSequence?.name}</p>
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Recipients</h3>
                <div className="max-h-24 overflow-y-auto rounded-lg border bg-muted/30 p-3">
                  <p className="text-sm">{emails}</p>
                </div>
              </div>
              <div>
                <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
                  Templates ({templates.length})
                </h3>
                <div className="space-y-2">
                  {templates.map((template, index) => (
                    <div
                      key={template.id}
                      className={`flex cursor-pointer items-center justify-between rounded-lg border p-3 transition-colors ${
                        index === previewTemplateIndex
                          ? 'border-primary bg-primary/5'
                          : 'hover:bg-muted/50'
                      }`}
                      onClick={() => handlePreviewTemplateChange(index)}
                    >
                      <div>
                        <p className="text-sm font-medium">{template.name}</p>
                        <p className="text-xs text-muted-foreground">{template.subject}</p>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {template.delayLabel}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-muted-foreground">
              Email Preview: {templates[previewTemplateIndex]?.name}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-hidden rounded-b-lg border-t">
              <iframe
                srcDoc={previewHtml}
                title="Email Preview"
                className="h-[500px] w-full"
                sandbox="allow-same-origin"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button onClick={handleSend} disabled={isSending}>
            {isSending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Send {templates.length} Email{templates.length !== 1 ? 's' : ''} to {emailList.length} Recipient
                {emailList.length !== 1 ? 's' : ''}
              </>
            )}
          </Button>
          <Button variant="outline" onClick={() => setMode('select')} disabled={isSending}>
            Back to Edit
          </Button>
        </div>
      </div>
    )
  }

  // Select view (default)
  return (
    <div className="space-y-6">
      {/* Sequence Selector */}
      <div>
        <label className="mb-2 block text-sm font-medium">Email Sequence</label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              {selectedSequence ? (
                <span>{selectedSequence.name}</span>
              ) : (
                <span className="text-muted-foreground">Select a sequence...</span>
              )}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[400px]">
            {sequences.map(sequence => (
              <DropdownMenuItem
                key={sequence.id}
                onClick={() => handleSequenceSelect(sequence)}
                className="flex flex-col items-start gap-1 py-3"
              >
                <span className="font-medium">{sequence.name}</span>
                <span className="text-xs text-muted-foreground">{sequence.description}</span>
                <span className="text-xs text-muted-foreground">
                  {sequence.templates.length} template{sequence.templates.length !== 1 ? 's' : ''}
                </span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Template Selection */}
      {selectedSequence && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <label className="text-sm font-medium">Templates</label>
            <div className="flex items-center gap-2">
              <Checkbox
                id="select-all"
                checked={selectAll}
                onCheckedChange={handleSelectAllToggle}
              />
              <label htmlFor="select-all" className="text-sm text-muted-foreground cursor-pointer">
                Select all ({selectedSequence.templates.length})
              </label>
            </div>
          </div>

          <div className="space-y-2">
            {selectedSequence.templates.map(template => (
              <Card
                key={template.id}
                className={`cursor-pointer transition-colors ${
                  selectedTemplateIds.includes(template.id)
                    ? 'border-primary bg-primary/5'
                    : 'hover:bg-muted/50'
                }`}
                onClick={() => handleTemplateToggle(template.id)}
              >
                <CardContent className="flex items-center gap-4 p-4">
                  <Checkbox
                    checked={selectedTemplateIds.includes(template.id)}
                    onCheckedChange={() => handleTemplateToggle(template.id)}
                    onClick={e => e.stopPropagation()}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <p className="font-medium truncate">{template.name}</p>
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{template.subject}</p>
                    <p className="text-xs text-muted-foreground mt-1">{template.description}</p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                    <Clock className="h-3 w-3" />
                    {template.delayLabel}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recipients */}
      <div>
        <label htmlFor="recipients" className="mb-2 block text-sm font-medium">
          Recipients
        </label>
        <Input
          id="recipients"
          type="text"
          placeholder="email1@example.com, email2@example.com"
          value={emails}
          onChange={e => setEmails(e.target.value)}
        />
        {emailList.length > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">{emailList.length} recipient(s)</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          onClick={handlePreview}
          disabled={!selectedSequence || selectedTemplateIds.length === 0 || !emails.trim()}
        >
          <Eye className="mr-2 h-4 w-4" />
          Preview & Send
        </Button>
      </div>

      {/* Info box */}
      {selectedSequence && selectedTemplateIds.length > 0 && (
        <Card className="bg-muted/30">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> When sending multiple templates, emails will be scheduled according to
              their delay settings. The first email is sent immediately, and subsequent emails are
              scheduled automatically.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
